import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Form as AntdForm, Modal, Steps } from 'antd'; // Import Antd Form
import { useTranslation } from 'react-i18next';
import Form from '../../../../../antd/form/Form/Form'; // Váš vlastný Form
import FormFileUpload from '../../../../../antd/form/FormFileUpload/FormFileUpload';
import { IconButton } from '../../../../../common/controls/Controls';
import { FormOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../../../../../utils/helpers/ApiClient';
import { useLazyGetErasmusMobilityPrivateFileQuery } from '../../../../../../redux/services/erasmusMobilities';
import { useToastMessages } from '../../../../../../hooks/useToastMessages.jsx';

const MOBILITY_STEP_FIELDS = [
  'ola_path',
  'financial_contract_path',
  'certificate_of_arrival_path',
  'certificate_of_completion_path',
  'grades_path',
  'questionnaire',
  'recognition_of_subjects_path',
  'recognition_of_study_path',
];

const PDF_FILE_ACCEPT = '.pdf,application/pdf';
const PDF_MAX_SIZE = 10 * 1024 * 1024;
const PDF_HINT = 'Maximálna veľkosť súboru je 10 MB vo formáte .pdf';

const DocumentsModal = ({
  visible,
  toggleModal,
  title,
  data,
  initialStepIndex,
  isLoading,
  onSave,
}) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [completedStep, setCompletedStep] = useState(0);
  const [uploadResetKeys, setUploadResetKeys] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const uploadInProgress = useRef(false);
  const [mobilityData, setMobilityData] = useState(data);
  const user = useSelector((state) => state.auth.user);
  const isRectorate = user?.roles?.some(
    (role) => role?.name === 'Rektorát Erasmus',
  );
  const [form] = AntdForm.useForm();
  const navigate = useNavigate();
  const { errorMessage, errorOccurred } = useToastMessages();
  const [triggerDownload] = useLazyGetErasmusMobilityPrivateFileQuery();
  const archived = Boolean(mobilityData?.application?.archived_at);

  const countStep = (sourceData) =>
    MOBILITY_STEP_FIELDS.filter((key) => sourceData?.[key]).length;

  const getLastAvailableStep = (completedCount) =>
    Math.min(completedCount, MOBILITY_STEP_FIELDS.length - 1);

  const resetUpload = (name) => {
    form.resetFields([name]);
    setUploadResetKeys((previousKeys) => ({
      ...previousKeys,
      [name]: (previousKeys[name] || 0) + 1,
    }));
  };

  const handleUpload = async (info, name) => {
    if (info) {
      if (uploadInProgress.current) {
        return;
      }

      uploadInProgress.current = true;
      setIsUploading(true);

      try {
        if (info.name?.split('.').pop()?.toLowerCase() !== 'pdf') {
          resetUpload(name);
          errorMessage({
            message: t('Je možné nahrať iba PDF súbor.'),
            key: 'erasmus-mobility-invalid-pdf',
          });
          return;
        }

        const updatedData = {
          ...mobilityData,
          [name]: info,
          name: name,
        };
        const savedData = await onSave({
          body: updatedData,
          id: mobilityData.id,
        });
        if (!savedData) {
          resetUpload(name);
          return;
        }
        const nextData = { ...updatedData, ...savedData };
        setMobilityData(nextData);
        const nextStep = countStep(nextData);
        setCompletedStep(nextStep);
        setCurrentStep(getLastAvailableStep(nextStep));
      } finally {
        uploadInProgress.current = false;
        setIsUploading(false);
      }
    } else {
      errorOccurred();
    }
  };

  const handlePreview = async (file) => {
    try {
      const mobilityId = mobilityData.id;
      const filename = file.name;

      const result = await triggerDownload(
        {
          id: mobilityId,
          filename,
        },
        true,
      );

      const byteCharacters = atob(result.data.pdf);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } catch (error) {
      console.error('Preview error:', error);
    }
  };

  useEffect(() => {
    if (data) {
      setMobilityData(data);
      const nextCompletedStep = countStep(data);
      setCompletedStep(nextCompletedStep);
      const normalizedInitialStep =
        Number.isInteger(initialStepIndex) &&
        initialStepIndex >= 0 &&
        initialStepIndex < MOBILITY_STEP_FIELDS.length
          ? Math.min(initialStepIndex, getLastAvailableStep(nextCompletedStep))
          : getLastAvailableStep(nextCompletedStep);
      setCurrentStep(normalizedInitialStep);
    }
  }, [data, initialStepIndex]);

  const steps = [
    {
      title: t('Krok 1: OLA'),
      content: (
        <AntdForm.Item // pouzijem AntdForm.Item
          form={form}
          label={t('Nahrajte Online learning agreement zmluvu')}
          name="ola_path"
          rules={[{ required: true, message: t('Prosím, nahrajte súbor!') }]}
        >
          <FormFileUpload
            key={`ola_path-${uploadResetKeys.ola_path || 0}`}
            name="ola_path"
            accept={PDF_FILE_ACCEPT}
            maxSize={PDF_MAX_SIZE}
            hintText={t(PDF_HINT)}
            onChange={(record) => handleUpload(record, 'ola_path')}
            disabled={isUploading || archived || isRectorate}
            onPreview={(file) => handlePreview(file)}
          />
        </AntdForm.Item>
      ),
    },
    {
      title: t('Krok 2: Finančná zmluva'),
      content: (
        <AntdForm.Item
          form={form}
          label={t('Nahrajte finančnú zmluvu')}
          name="financial_contract_path"
          rules={[{ required: true, message: t('Prosím, nahrajte súbor!') }]}
        >
          <FormFileUpload
            key={`financial_contract_path-${uploadResetKeys.financial_contract_path || 0}`}
            name="financial_contract_path"
            accept={PDF_FILE_ACCEPT}
            maxSize={PDF_MAX_SIZE}
            hintText={t(PDF_HINT)}
            onChange={(record) =>
              handleUpload(record, 'financial_contract_path')
            }
            disabled={
              isUploading || completedStep < 1 || archived || isRectorate
            }
            onPreview={(file) => handlePreview(file)}
          />
        </AntdForm.Item>
      ),
    },
    {
      title: t('Krok 3: Potvrdenie o príchode'),
      content: (
        <AntdForm.Item
          form={form}
          label={t('Nahrajte potvrdenie o príchode')}
          name="certificate_of_arrival_path"
          rules={[{ required: true, message: t('Prosím, nahrajte súbor!') }]}
        >
          <FormFileUpload
            key={`certificate_of_arrival_path-${uploadResetKeys.certificate_of_arrival_path || 0}`}
            name="certificate_of_arrival_path"
            accept={PDF_FILE_ACCEPT}
            maxSize={PDF_MAX_SIZE}
            hintText={t(PDF_HINT)}
            onChange={(record) =>
              handleUpload(record, 'certificate_of_arrival_path')
            }
            disabled={
              isUploading || completedStep < 2 || archived || isRectorate
            }
            onPreview={(file) => handlePreview(file)}
          />
        </AntdForm.Item>
      ),
    },
    {
      title: t('Krok 4: Potvrdenie o ukončení mobility'),
      content: (
        <AntdForm.Item
          form={form}
          label={t('Nahrajte potvrdenie o ukončení mobility')}
          name="certificate_of_completion_path"
          rules={[{ required: true, message: t('Prosím, nahrajte súbor!') }]}
        >
          <FormFileUpload
            key={`certificate_of_completion_path-${uploadResetKeys.certificate_of_completion_path || 0}`}
            name="certificate_of_completion_path"
            accept={PDF_FILE_ACCEPT}
            maxSize={PDF_MAX_SIZE}
            hintText={t(PDF_HINT)}
            onChange={(record) =>
              handleUpload(record, 'certificate_of_completion_path')
            }
            disabled={
              isUploading || completedStep < 3 || archived || isRectorate
            }
            onPreview={(file) => handlePreview(file)}
          />
        </AntdForm.Item>
      ),
    },
    {
      title: t('Krok 5: Potvrdenie o študijných výsledkoch'),
      content: (
        <AntdForm.Item
          form={form}
          label={t('Nahrajte potvrdenie o študijných výsledkoch')}
          name="grades_path"
          rules={[{ required: true, message: t('Prosím, nahrajte súbor!') }]}
        >
          <FormFileUpload
            key={`grades_path-${uploadResetKeys.grades_path || 0}`}
            name="grades_path"
            accept={PDF_FILE_ACCEPT}
            maxSize={PDF_MAX_SIZE}
            hintText={t(PDF_HINT)}
            onChange={(record) => handleUpload(record, 'grades_path')}
            disabled={
              isUploading || completedStep < 4 || archived || isRectorate
            }
            onPreview={(file) => handlePreview(file)}
          />
        </AntdForm.Item>
      ),
    },
    {
      title: t('Krok 6: Dotazník'),
      content: (
        <AntdForm.Item
          form={form}
          label={t('Vyplňte dotazník o mobilite')}
          name="questionnaire"
          rules={[{ required: true, message: t('Prosím, vyplňte dotazník!') }]}
          style={{ textAlign: 'center' }} // Vycentrovanie formulára
        >
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <IconButton
              icon={<FormOutlined />}
              tooltip={t('Vyplňte dotazník')}
              name={t('Dotazník')}
              style={{
                padding: '20px 40px',
                borderRadius: '8px',
                minWidth: '700px',
                height: '100px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              disabled={
                isUploading || completedStep < 5 || archived || isRectorate
              }
              disabledTooltip={
                archived
                  ? t('Dotazník bol archivovaný')
                  : t(
                      'Dotazník je možné vyplniť až po nahratí všetkých predchádzajúcich dokumentov.',
                    )
              }
              onClick={() => {
                mobilityData?.questionnaire
                  ? navigate(
                      `${routes.cmsErasmusMobilityQuestionnaireEdit}/${mobilityData.questionnaire.id}`,
                    )
                  : navigate(
                      `${routes.cmsErasmusMobilityQuestionnaireCreate}/${mobilityData.id}`,
                    );
              }}
            />
          </div>
        </AntdForm.Item>
      ),
    },
    {
      title: t('Krok 7: Žiadosť o uznanie predmetov'),
      content: (
        <AntdForm.Item
          form={form}
          label={t('Nahrajte žiadosť o uznanie predmetov')}
          name="recognition_of_subjects_path"
          rules={[{ required: true, message: t('Prosím, nahrajte súbor!') }]}
        >
          <FormFileUpload
            key={`recognition_of_subjects_path-${uploadResetKeys.recognition_of_subjects_path || 0}`}
            name="recognition_of_subjects_path"
            accept={PDF_FILE_ACCEPT}
            maxSize={PDF_MAX_SIZE}
            hintText={t(PDF_HINT)}
            onChange={(record) =>
              handleUpload(record, 'recognition_of_subjects_path')
            }
            disabled={completedStep < 5 || archived || isRectorate}
            onPreview={(file) => handlePreview(file)}
          />
        </AntdForm.Item>
      ),
    },
    {
      title: t('Krok 8: Uznanie mobility ako súčasť štúdia'),
      content: (
        <AntdForm.Item
          form={form}
          label={t('Nahrajte súbor o uznaní mobility ako súčasť štúdia')}
          name="recognition_of_study_path"
          rules={[{ required: true, message: t('Prosím, nahrajte súbor!') }]}
        >
          <FormFileUpload
            key={`recognition_of_study_path-${uploadResetKeys.recognition_of_study_path || 0}`}
            name="recognition_of_study_path"
            accept={PDF_FILE_ACCEPT}
            maxSize={PDF_MAX_SIZE}
            hintText={t(PDF_HINT)}
            onChange={(record) =>
              handleUpload(record, 'recognition_of_study_path')
            }
            disabled={
              isUploading || completedStep < 6 || archived || isRectorate
            }
            onPreview={(file) => handlePreview(file)}
          />
        </AntdForm.Item>
      ),
    },
  ];

  const stepsWithDisabledState = steps.map((step, index) => ({
    ...step,
    disabled: index > getLastAvailableStep(completedStep),
  }));

  return (
    <Modal
      open={visible}
      title={title || t('Dokumenty mobility')}
      onCancel={toggleModal}
      footer={null}
      width={800}
    >
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          {t('Načítavam')}...
        </div>
      ) : (
        <Form form={form} initialValues={mobilityData}>
          <Steps
            orientation="vertical"
            size="small"
            current={currentStep}
            items={stepsWithDisabledState}
            onChange={(nextStep) => {
              if (nextStep <= getLastAvailableStep(completedStep)) {
                setCurrentStep(nextStep);
              }
            }}
          />
        </Form>
      )}
    </Modal>
  );
};

DocumentsModal.propTypes = {
  visible: PropTypes.bool,
  toggleModal: PropTypes.func,
  title: PropTypes.string,
  data: PropTypes.object,
  initialStepIndex: PropTypes.number,
  isLoading: PropTypes.bool,
  onSave: PropTypes.func,
};

export default DocumentsModal;
